import { useCallback, useState } from "react"
import { useMutation } from "urql"

import { CreateLocationMutationDocument, UpdateLocationMutationDocument } from "@/graphql/codegen/graphql"
import type {
  CreateLocationMutationMutation,
  CreateLocationMutationMutationVariables,
  Location,
  LocationUpdateInput,
  UpdateLocationMutationMutation,
  UpdateLocationMutationMutationVariables,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type { LocationMenuDialogs } from "@/screens/Companies/edit/components/locations/types/dialogs"
import { LocationConfirmationDialogs } from "@/screens/Companies/edit/components/locations/types/dialogs"
import { useGetCompanies } from "@/screens/Companies/hooks/useGetCompanies"
import type {
  AddAddressQueryProps,
  EditAddressQueryProps,
  EditContactQueryProps,
  EditNotesQueryProps,
  LocationCreateFormValue,
  UpdateAccountManagerProps,
  UpdateSalesmanProps,
} from "@/screens/Companies/types/dialogProps"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export const useLocationTableActions = ({
  companyId,
  reexecuteQueryWithReset,
}: {
  companyId: string
  reexecuteQueryWithReset: () => void
}): {
  openedDialog: LocationMenuDialogs | LocationConfirmationDialogs | null
  setOpenedDialog: (dialog: LocationMenuDialogs | LocationConfirmationDialogs | null) => void
  prevOpenedDialog: LocationMenuDialogs | LocationConfirmationDialogs | null
  activeEditLocation: Location | undefined
  setActiveEditLocation: (location: Location) => void
  handleOnUpdateSubmit: (
    data:
      | EditContactQueryProps
      | EditAddressQueryProps
      | EditNotesQueryProps
      | UpdateAccountManagerProps
      | UpdateSalesmanProps
  ) => Promise<void>
  handleOnCreateSubmit: (data: AddAddressQueryProps) => Promise<void>
} => {
  const [openedDialog, setOpenedDialog] = useState<LocationMenuDialogs | LocationConfirmationDialogs | null>(null)
  const [prevOpenedDialog, setPrevOpenedDialog] = useState<LocationMenuDialogs | LocationConfirmationDialogs | null>(
    null
  )
  const { reexecuteCompanyDetailsQuery } = useGetCompanies(companyId)
  const [activeEditLocation, setActiveEditLocation] = useState<Location | undefined>()
  const triggerNotification = useNotificationsStore((store) => store.enqueueNotification)

  const [, createLocation] = useMutation<CreateLocationMutationMutation, CreateLocationMutationMutationVariables>(
    CreateLocationMutationDocument
  )

  const [, updateLocation] = useMutation<UpdateLocationMutationMutation, UpdateLocationMutationMutationVariables>(
    UpdateLocationMutationDocument
  )

  const handleSetOpenedDialog = useCallback(
    (dialog: LocationMenuDialogs | LocationConfirmationDialogs | null) => {
      setPrevOpenedDialog(openedDialog)
      setOpenedDialog(dialog)
    },
    [openedDialog]
  )

  const handleOnUpdateSubmit = useCallback(
    async (
      data:
        | EditContactQueryProps
        | EditAddressQueryProps
        | EditNotesQueryProps
        | UpdateAccountManagerProps
        | UpdateSalesmanProps
    ) => {
      const { __typename, ...activeLocationWithoutTypename } = activeEditLocation as Location

      const { accountManager, salesman, as400, ...location } = data

      const input: LocationUpdateInput = {
        companyId,
        locationId: activeLocationWithoutTypename.locationId,
        as400Address1: as400?.address1,
        as400Address2: as400?.address2,
        as400City: as400?.city,
        as400Name: as400?.name,
        as400State: as400?.regionIsoCode,
        as400PostalCode: as400?.postalCode,
        ...location,
      }

      if (accountManager !== undefined) {
        input.accountManagerId = accountManager?.id ? accountManager.id : null
      }

      if (salesman !== undefined) {
        input.salesmanNbr = salesman?.value ? Number(salesman.value) : null
      }

      const res = await updateLocation({ input })
      if (res.error || res.data?.location.update.__typename === "LocationUpdateFailure") {
        triggerNotification({
          message: "Failed to update location",
          type: NotificationType.Error,
        })
        return
      }

      triggerNotification({
        message: "Location updated successfully",
        type: NotificationType.Success,
      })

      reexecuteQueryWithReset()
      reexecuteCompanyDetailsQuery()
      setOpenedDialog(null)
    },
    [
      activeEditLocation,
      companyId,
      updateLocation,
      triggerNotification,
      reexecuteQueryWithReset,
      reexecuteCompanyDetailsQuery,
    ]
  )

  const handleOnCreateSubmit = useCallback(
    async (data: LocationCreateFormValue) => {
      const { accountManager, salesman, as400, ...location } = data

      const input = {
        ...location,
        accountManagerId: accountManager ? accountManager.id : null,
        salesmanNbr: salesman ? Number(salesman.value) : null,
        as400Address1: as400?.address1,
        as400Address2: as400?.address2,
        as400City: as400?.city,
        as400Name: as400?.name,
        as400State: as400?.regionIsoCode,
        as400PostalCode: as400?.postalCode,
        isActive: true,
        companyId,
      }

      const res = await createLocation({ input })

      if (res.error || res.data?.location.create.__typename === "LocationCreateFailure") {
        triggerNotification({
          message: "Failed to create location",
          type: NotificationType.Error,
        })
        return
      }
      reexecuteQueryWithReset()
      reexecuteCompanyDetailsQuery()
      setOpenedDialog(LocationConfirmationDialogs.SUCCESS)
    },
    [companyId, createLocation, reexecuteQueryWithReset, reexecuteCompanyDetailsQuery, triggerNotification]
  )

  return {
    openedDialog,
    setOpenedDialog: handleSetOpenedDialog,
    prevOpenedDialog,
    activeEditLocation,
    setActiveEditLocation,
    handleOnUpdateSubmit,
    handleOnCreateSubmit,
  }
}
